import { motion } from "motion/react";
import { useRef, useEffect } from "react";
import type { Project } from "../types/Project";

type ProjectModalProps = {
  project: Project;
  selectedImage: number;
  onClose: () => void;
  onSelectImage: (n: number) => void;
  onImagePreview: (url: string) => void;
};

export function ProjectModal({
  project,
  selectedImage,
  onClose,
  onSelectImage,
  onImagePreview,
}: ProjectModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[#0f0f0f]/90 backdrop-blur-sm" />

      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0f0f0f] border border-white/15 outline-none"
      >
        {/* modal header */}
        <div className="sticky top-0 bg-[#0f0f0f]/95 backdrop-blur border-b border-white/15 px-5 py-3.5 flex items-center justify-between gap-4 z-10">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold tracking-tight">
              {project.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="border border-white/15 px-2.5 py-1 text-xs text-white/50 hover:text-white hover:border-white/30 transition-colors duration-200"
          >
            lukk
          </button>
        </div>

        {/* modal content */}
        <div className="p-5">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-5 items-start">
            {/* left: media */}
            <div className="space-y-2.5">
              <button
                onClick={() =>
                  onImagePreview(
                    `/screenshots/okktav/okktav-${selectedImage}.png`,
                  )
                }
                className="border border-white/15 aspect-video bg-[#0f0f0f] overflow-hidden hover:border-white/30 transition-colors duration-200 w-full"
              >
                <img
                  src={`/screenshots/okktav/okktav-${selectedImage}.png`}
                  alt={`${project.title} screenshot ${selectedImage}`}
                  className="w-full h-full object-contain"
                />
              </button>

              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    onClick={() => onSelectImage(n)}
                    className={`aspect-square border border-white/15 overflow-hidden transition-colors duration-200 bg-[#0f0f0f] p-1 ${
                      n === selectedImage
                        ? "border-white/30 bg-white/3"
                        : "hover:border-white/30"
                    }`}
                  >
                    <img
                      src={`/screenshots/okktav/okktav-${n}.png`}
                      alt={`${project.title} thumbnail ${n}`}
                      className="w-full h-full object-contain"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* right: info */}
            <div className="space-y-3.5">
              <p className="text-white/70 leading-relaxed text-sm">
                {project.description}
              </p>

              <ul className="space-y-2 text-sm text-white/70">
                {project.features.map((feature, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-white/30">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
